/* Is this GIF animated?
 *
 * WHY IT MATTERS. The drop zone advertises GIF, and a GIF converted to any
 * other format comes back as ONE FRAME -- measured: 24 frames in, 1 frame out,
 * and it is a property of asking for a format at all, not of the format asked
 * for (f_webp flattens an animation exactly as f_jpg does). So somebody drops
 * an animation, gets a still, and nothing says why. This is what lets the page
 * say why.
 *
 * A BLOCK WALK, not a byte search. Counting occurrences of the Graphic Control
 * Extension marker (21 F9 04) is three lines and wrong: those bytes occur in
 * LZW image data by coincidence, and a false "this is animated" on somebody's
 * static logo is worse than saying nothing. So this walks the structure --
 * header, logical screen descriptor, then blocks -- and counts IMAGE
 * DESCRIPTORS, which is what a frame actually is.
 *
 * Stops at two. The question is "more than one?", not "how many?", so nothing
 * reads further than it must.
 */

const GIF87A = "GIF87a"
const GIF89A = "GIF89a"

const EXTENSION = 0x21
const IMAGE_DESCRIPTOR = 0x2c
const TRAILER = 0x3b

/* A colour table is 3 bytes per entry, and the entry count is 2^(N+1) where N
 * is the low three bits of the flags byte. Both tables -- the global one after
 * the screen descriptor and a local one on a frame -- are sized this way. */
function colourTableBytes(flags) {
    return (flags & 0x80) ? 3 * (1 << ((flags & 0x07) + 1)) : 0
}

/* Sub-blocks are length-prefixed and terminated by a zero length. Returns the
 * offset just past the terminator, or -1 if the data runs out first -- a
 * truncated file is not an animation claim we should make. */
function skipSubBlocks(bytes, at) {
    let i = at
    while (i < bytes.length) {
        const len = bytes[i]
        if (len === 0) {
            return i + 1
        }
        i += 1 + len
    }
    return -1
}

/* Returns 0, 1 or 2 -- where 2 means "at least two", which is all the caller
 * needs. Returns 0 for anything that is not a GIF or cannot be walked, so a
 * malformed file is never reported as animated. */
export function countGifFrames(buffer) {
    const bytes = new Uint8Array(buffer)
    if (bytes.length < 13) {
        return 0
    }
    let magic = ""
    for (let i = 0; i < 6; i++) {
        magic += String.fromCharCode(bytes[i])
    }
    if (magic !== GIF87A && magic !== GIF89A) {
        return 0
    }

    // 6 header + 7 logical screen descriptor; the flags byte is the 5th of those
    let at = 13 + colourTableBytes(bytes[10])
    let frames = 0

    while (at < bytes.length) {
        const marker = bytes[at]

        if (marker === TRAILER) {
            break
        }

        if (marker === EXTENSION) {
            // introducer, label, then sub-blocks
            at = skipSubBlocks(bytes, at + 2)
            if (at < 0) {
                break
            }
            continue
        }

        if (marker === IMAGE_DESCRIPTOR) {
            frames += 1
            if (frames >= 2) {
                return 2
            }
            // 1 marker + 8 of position and size + 1 flags
            const flags = bytes[at + 9]
            // ...then a local colour table, the LZW code size, and the data
            at = skipSubBlocks(bytes, at + 10 + colourTableBytes(flags) + 1)
            if (at < 0) {
                break
            }
            continue
        }

        // Anything else means the walk has lost the structure. Say nothing
        // rather than guess.
        return 0
    }

    return frames
}

/* The question a caller actually has, answered off a File.
 *
 * Reads the whole file, and only for a GIF: a second frame's descriptor sits
 * after the first frame's compressed data, so a slice of the front cannot
 * answer this for a large animation, and answering "no" from a truncated read
 * would be the wrong way to be wrong. Resolves false on any failure.
 */
export async function isAnimatedGif(file) {
    if (!file) {
        return false
    }
    const name = String(file.name || "").toLowerCase()
    const type = String(file.type || "").toLowerCase()
    if (type !== "image/gif" && !name.endsWith(".gif")) {
        return false
    }
    try {
        return countGifFrames(await file.arrayBuffer()) >= 2
    } catch (err) {
        return false
    }
}
